import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** 1 ページの件数。 */
export const USERS_PAGE_SIZE = 50;

export interface UserListParamsState {
  page: number;
  keyword: string;
  sortKey: string;
  ascending: boolean;
}

const DEFAULTS: UserListParamsState = {
  page: 1,
  keyword: "",
  sortKey: "email",
  ascending: true,
};

/**
 * ユーザー一覧の検索条件・並び替え・ページを URL のクエリに持つ。
 *
 * 他の一覧画面（[[useQuotationListParams]] / [[usePartnerListParams]]）と同じ方針。
 */
export function useUserListParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<UserListParamsState>(() => {
    const page = Number(searchParams.get("page") ?? DEFAULTS.page);
    return {
      page: Number.isFinite(page) && page >= 1 ? page : DEFAULTS.page,
      keyword: searchParams.get("keyword") ?? DEFAULTS.keyword,
      sortKey: searchParams.get("sort_key") ?? DEFAULTS.sortKey,
      ascending: (searchParams.get("ascending") ?? "true") === "true",
    };
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<UserListParamsState>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged = { ...params, ...patch };
          // 条件を変えたらページを 1 に戻す（page 自体の変更を除く）。
          if (patch.page === undefined) merged.page = 1;

          const set = (key: string, value: string, fallback: string) =>
            value === fallback ? next.delete(key) : next.set(key, value);

          set("page", String(merged.page), String(DEFAULTS.page));
          set("keyword", merged.keyword, DEFAULTS.keyword);
          set("sort_key", merged.sortKey, DEFAULTS.sortKey);
          set("ascending", String(merged.ascending), String(DEFAULTS.ascending));
          return next;
        },
        { replace: true },
      );
    },
    [params, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  return { params, update, reset };
}
